import { IDropdownOption } from '@fluentui/react';
import { useCallback, useState } from 'react';

import { AIEMOpType, AIEngine } from '../../../../../aiem/engine/types';
import { DAIEngine } from '../../../../../aiem/gen/ai/h2o/engine/v1/dai_engine_pb';
import { DAIEngineProfile } from '../../../../../aiem/gen/ai/h2o/engine/v1/dai_engine_profile_pb';
import { Engine_Type } from '../../../../../aiem/gen/ai/h2o/engine/v1/engine_pb';
import { H2OEngineProfile } from '../../../../../aiem/gen/ai/h2o/engine/v1/h2o_engine_profile_pb';
import { useEngineProfilesService } from '../../../../../aiem/hooks';
import { getIdFromName } from '../../../../../aiem/utils';
import { EngineProfileEntityModel } from '../../../../../pages/AIEngineSettingsPage/components/ListWithCollapsedSettings/types';
import { H2OEngineDefaultProfiles } from '../../../constants';

/**
 * Hook for managing engine profiles and configuration options.
 *
 * Responsibilities:
 * - Manages engine profiles and configuration options for all engine types
 * - Handles profile fetching and selection from the backend
 * - Provides constraint-based engine configuration generation
 * - Validates that selected profiles are available in the options list
 * - Manages H2O-specific dataset parameter configurations
 *
 * Features:
 * - Fetches enabled profiles from the backend with proper error handling
 * - Maps profile constraints to engine default values for create operations
 * - Preserves existing engine values for update operations
 *
 * @param engine - The current engine being configured
 * @param op - The operation type (create, update, view)
 * @returns Object containing profile state, handlers, and configuration utilities
 */
export const useEngineProfiles = (engine: AIEngine, op: AIEMOpType) => {
  const engineProfilesService = useEngineProfilesService();
  const [selectedProfileOption, setSelectedProfileOption] = useState<EngineProfileEntityModel>();
  const [profileOptions, setProfileOptions] = useState<IDropdownOption[]>([]);
  const [selectedConfigurationOption, setSelectedConfigurationOption] = useState<IDropdownOption>(
    H2OEngineDefaultProfiles[0]
  );

  const engineType = engine?.engineType;
  const isH2OEngine = engineType === Engine_Type.H2O;
  const isDAIEngine = engineType === Engine_Type.DRIVERLESS_AI;
  const isProfileInList = profileOptions.some((option) => option.key === selectedProfileOption?.name);

  const fetchProfileOptions = useCallback(async (): Promise<EngineProfileEntityModel | null> => {
    if (!engineType) return null;

    const responseKeyMap = {
      [Engine_Type.DRIVERLESS_AI]: 'daiEngineProfiles',
      [Engine_Type.H2O]: 'h2oEngineProfiles',
      [Engine_Type.NOTEBOOK]: 'notebookEngineProfiles',
    };

    const listEnginesMap = {
      [Engine_Type.DRIVERLESS_AI]: (params: any) => engineProfilesService.listAssignedDAIEngineProfiles(params),
      [Engine_Type.H2O]: (params: any) => engineProfilesService.listAssignedH2OEngineProfiles(params),
      [Engine_Type.NOTEBOOK]: (params: any) => engineProfilesService.listAssignedNotebookEngineProfiles(params),
    };

    let activeProfile: IDropdownOption;

    try {
      const validEngineType = engineType !== Engine_Type.UNSPECIFIED ? engineType : Engine_Type.DRIVERLESS_AI;
      const responseKey = responseKeyMap[validEngineType];
      const requestParams = { parent: 'workspaces/global', pageSize: 1000 };
      const listEnginesFn = listEnginesMap[validEngineType];
      const response = await listEnginesFn(requestParams);
      const enabledProfileOptions = ((response as any)[responseKey] || []).filter(
        (profile: DAIEngineProfile | H2OEngineProfile) => profile?.enabled
      );

      const profileMapper = (profile: DAIEngineProfile | H2OEngineProfile, showId = false) => ({
        key: profile.name || '',
        text: `${profile.displayName} ${showId ? `(${getIdFromName(profile.name)})` : ''}`,
        data: profile,
      });

      const profileOptionsData = enabledProfileOptions.map((profile: DAIEngineProfile | H2OEngineProfile) =>
        profileMapper(profile, true)
      );

      if (op === AIEMOpType.create) {
        activeProfile = profileOptionsData[0];
      } else {
        activeProfile = profileOptionsData.find((profile: IDropdownOption) => profile.key === engine?.profile) ?? {
          data: { name: engine?.profile },
        };
      }

      setProfileOptions(profileOptionsData);
      setSelectedProfileOption(activeProfile.data);

      return activeProfile.data;
    } catch (error) {
      console.error(error);
      return null;
    }
  }, [engineType, op, engine?.profile, engineProfilesService]);

  const getEngineFromConstraintSet = useCallback(
    (constraints: EngineProfileEntityModel): Partial<AIEngine> => {
      if (!op || !engine) return {};

      return op === AIEMOpType.create
        ? {
            cpu: Number(constraints?.cpuConstraint?.default),
            gpu: Number(constraints?.gpuConstraint?.default),
            memoryBytes: constraints?.memoryBytesConstraint?.default,
            storageBytes: (constraints as DAIEngineProfile)?.storageBytesConstraint?.default,
            maxIdleDuration: String(constraints?.maxIdleDurationConstraint?.default),
            maxRunningDuration: String(constraints?.maxRunningDurationConstraint?.default),
            config: (constraints as DAIEngineProfile)?.baseConfiguration,
            ...(isH2OEngine
              ? { nodeCount: Number((constraints as H2OEngineProfile)?.nodeCountConstraint?.default) }
              : {}),
          }
        : {
            cpu: Number(engine.cpu),
            gpu: Number(engine.gpu),
            memoryBytes: engine.memoryBytes,
            storageBytes: engine.storageBytes,
            maxIdleDuration: engine.maxIdleDuration,
            maxRunningDuration: engine.maxRunningDuration,
            ...(isDAIEngine ? { config: (engine as DAIEngine).config } : {}),
            ...(isH2OEngine ? { nodeCount: Number(engine.nodeCount) } : {}),
          };
    },
    [op, engine, isH2OEngine, isDAIEngine]
  );

  const handleProfileOptionSelect = useCallback((_: any, option: EngineProfileEntityModel) => {
    setSelectedProfileOption(option);
    // Note: handleConfigurationOptionSelect will be called separately to avoid circular dependency
  }, []);

  const handleConfigurationOptionSelect = useCallback((_: any, option?: IDropdownOption) => {
    if (!option) return;
    setSelectedConfigurationOption(option);
  }, []);

  return {
    selectedProfileOption,
    setSelectedProfileOption,
    profileOptions,
    selectedConfigurationOption,
    setSelectedConfigurationOption,
    isProfileInList,
    fetchProfileOptions,
    getEngineFromConstraintSet,
    handleProfileOptionSelect,
    handleConfigurationOptionSelect,
    isH2OEngine,
    isDAIEngine,
  };
};
